"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import Toggle from "react-toggle";
import "react-toggle/style.css";
import classnames from "classnames";
import {
  doc,
  getFirestore,
  collection,
  query,
  onSnapshot,
  getDoc,
} from "firebase/firestore";
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from "firebase/functions";
import { orderBy } from "lodash";
import * as Sentry from "@sentry/nextjs";

import { useAuthContext } from "../../../../../context/AuthContext";
import firebase_app from "../../../../../firebase/config";
import { Logo } from "../../../../components/logo/Logo";
import { SignInModal } from "../../../../components/sign-in-modal/SignInModal";
import { ShareModal } from "../../../../components/share-modal/ShareModal";
import { ClickUpModal } from "../../../../components/clickup-modal/ClickUpModal";
import { AuthorModal } from "../../../../components/author-modal/AuthorModal";

import { Comment } from "./comment/Comment";
import { ActiveComment } from "./active-comment/ActiveComment";
import { Integration } from "./integration/Integration";
import styles from "./styles.module.css";

const functions = getFunctions(firebase_app);
const db = getFirestore(firebase_app);

if (process.env.NODE_ENV === "development") {
  connectFunctionsEmulator(functions, "localhost", 5001);
}

const getSnapshotURL = (projectId, url, isDemo) =>
  `https://storage.googleapis.com/sima-aa95b.appspot.com/snapshots/${projectId}/${btoa(
    url,
  )}/index.html?isDemo=${isDemo}`;

const extractPageURL = (snapshotURL) => {
  // Split the URL by the "snapshots/" segment
  const parts = snapshotURL.split("snapshots/");

  // Ensure there's a second part after "snapshots/"
  if (parts.length > 1) {
    const afterSnapshots = parts[1];

    // Split the remaining string by "/" and return the second segment
    const base64Segment = afterSnapshots.split("/")[1];
    return atob(base64Segment);
  }

  // If the URL doesn't have the expected format, return null or throw an error
  return null;
};

export default function Project({ params: { projectid: projectId } }) {
  const { user } = useAuthContext();
  const searchParams = useSearchParams();
  const isDemo = searchParams.get("isDemo");

  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showClickUpModal, setShowClickUpModal] = useState(false);
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [commentMode, setCommentMode] = useState(true);
  const [comments, setComments] = useState([]);
  const [hasFetchedComments, setHasFetchedComments] = useState(false);
  const [highlightCommentId, setHighlightCommentId] = useState(null);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [sidebarTab, setSidebarTab] = useState("active");
  const iframeRef = useRef(null);
  const commentModeRef = useRef(commentMode);
  // const [pauseNewComments, setPauseNewComments] = useState(false);

  useEffect(() => {
    commentModeRef.current = commentMode;
  }, [commentMode]);

  const [iframeSrc, setIframeSrc] = useState(null);

  const activeComment = comments.find(
    (comment) => comment.id === activeCommentId,
  );

  const unResolvedComments = comments.filter((comment) => !comment.resolved);
  const unResolvedCommentsSorted = orderBy(
    unResolvedComments,
    "timestamp",
    "asc",
  );
  const resolvedComments = comments.filter((comment) => comment.resolved);
  const resolvedCommentsSorted = orderBy(resolvedComments, "timestamp", "desc");

  const hasNoComments =
    !unResolvedCommentsSorted.length && !resolvedCommentsSorted.length;

  const clonePage = async (linkClicked) => {
    try {
      const cloneWebsite = httpsCallable(functions, "cloneWebsite");
      const result = await cloneWebsite({ url: linkClicked, projectId });

      setIframeSrc(result.data.url);
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  };

  const fetchProjectURL = async () => {
    const docRef = doc(db, `projects/${projectId}`);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists && docSnap.data()?.projectURL) {
      setIframeSrc(
        getSnapshotURL(projectId, docSnap.data()?.projectURL, isDemo),
      );
    }
  };

  useEffect(() => {
    fetchProjectURL();
  }, []);

  useEffect(() => {
    const storedName = localStorage.getItem("personName");

    if (comments.length > 0 && !storedName) {
      setShowAuthorModal(true);
    }
  }, [comments.length]);

  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage({ activeCommentId }, "*");
    }
  }, [activeCommentId]);

  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage({ highlightCommentId }, "*");
    }
  }, [highlightCommentId]);

  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.contentWindow.postMessage({ commentMode }, "*");
    }
  }, [commentMode]);

  useEffect(() => {
    if (activeComment?.pageURL) {
      const parsedURL = extractPageURL(activeComment?.pageURL);
      clonePage(parsedURL);
    }
  }, [activeComment?.pageURL]);

  useEffect(() => {
    if (iframeRef.current) {
      const currentCommentMode = commentModeRef.current;

      const handleLoad = () => {
        iframeRef.current.contentWindow.postMessage(
          { commentMode: currentCommentMode, activeCommentId },
          "*",
        );
      };

      const iframe = iframeRef.current;
      iframe.addEventListener("load", handleLoad);

      return () => {
        iframe.removeEventListener("load", handleLoad);
      };
    }
  }, [iframeSrc, commentMode, activeCommentId]);

  useEffect(() => {
    const commentsCollection = `projects/${projectId}/comments`;
    const unsubscribe = onSnapshot(
      query(collection(db, commentsCollection)),
      (snapshot) => {
        const fetchedComments = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setComments(fetchedComments);
        setHasFetchedComments(true);
      },
      (error) => {
        console.error("Error fetching comments:", error);
        Sentry.captureException(error);
      },
    );

    return () => unsubscribe();
  }, [projectId]);

  useEffect(() => {
    const handleMessage = (event) => {
      const { activeCommentId, highlightCommentId, linkClicked } = event.data;

      if (linkClicked) {
        clonePage(linkClicked);
      }

      setActiveCommentId(activeCommentId);
      setHighlightCommentId(highlightCommentId);
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const renderActiveComments = () => {
    if (!unResolvedCommentsSorted.length) {
      return <div className={styles.noComments}>No active comments.</div>;
    }

    return unResolvedCommentsSorted.map((comment) => (
      <Comment
        key={comment.id}
        comment={comment}
        setActiveCommentId={setActiveCommentId}
        highlightCommentId={highlightCommentId}
        setHighlightCommentId={setHighlightCommentId}
      />
    ));
  };

  const renderResolvedComments = () => {
    if (!resolvedCommentsSorted.length) {
      return <div className={styles.noComments}>No resolved comments.</div>;
    }

    return resolvedCommentsSorted.map((comment) => (
      <Comment
        key={comment.id}
        comment={comment}
        setActiveCommentId={setActiveCommentId}
        highlightCommentId={highlightCommentId}
        setHighlightCommentId={setHighlightCommentId}
      />
    ));
  };

  return (
    <div className={styles.appContainer}>
      <SignInModal
        showModal={showSignInModal}
        setShowModal={setShowSignInModal}
      />
      <ShareModal showModal={showShareModal} setShowModal={setShowShareModal} />
      <ClickUpModal
        key={activeCommentId}
        showModal={showClickUpModal}
        setShowModal={setShowClickUpModal}
        commentId={activeCommentId}
        projectId={projectId}
        currentTaskId={activeComment?.clickupTaskId}
      />
      <AuthorModal
        showModal={showAuthorModal}
        setShowModal={setShowAuthorModal}
      />

      <header className={styles.header}>
        <div className={styles.logoContainer}></div>
        <div className={styles.toggleContainer}>
          <div>Browse</div>
          <Toggle
            className={styles.toggle}
            checked={commentMode}
            icons={false}
            onChange={(event) => setCommentMode(event.target.checked)}
          />
          <div>Comment</div>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.shareButton}
            onClick={() => setShowShareModal(true)}
          >
            Share
          </button>
        </div>
      </header>
      <main
        className={classnames(styles.container, {
          [styles.commentMode]: commentMode,
        })}
      >
        <div className={styles.sidebar}>
          {!hasNoComments && (
            <div className={styles.sidebarHeader}>
              {!activeCommentId && (
                <>
                  <div
                    className={classnames(styles.sidebarTab, {
                      [styles.activeTab]: sidebarTab === "active",
                    })}
                    onClick={() => setSidebarTab("active")}
                  >
                    {unResolvedCommentsSorted.length} Active
                  </div>
                  <div
                    className={classnames(styles.sidebarTab, {
                      [styles.activeTab]: sidebarTab === "resolved",
                    })}
                    onClick={() => setSidebarTab("resolved")}
                  >
                    {resolvedCommentsSorted.length} Resolved
                  </div>
                </>
              )}
              {activeCommentId && (
                <div className={styles.sidebarHeaderActive}>
                  <div
                    className={styles.sidebarBackButton}
                    onClick={() => setActiveCommentId(null)}
                  >
                    <svg
                      width="1rem"
                      height="1rem"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 12H19M5 12L11 6M5 12L11 18"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Back
                  </div>

                  <Integration
                    projectId={projectId}
                    comment={activeComment}
                    setShowClickUpModal={setShowClickUpModal}
                  />
                </div>
              )}
            </div>
          )}

          {hasFetchedComments && hasNoComments && (
            <div className={styles.noComments}>
              Click anywhere to add a comment.
            </div>
          )}

          {activeCommentId ? (
            <>
              <ActiveComment
                key={activeCommentId}
                projectId={projectId}
                comment={activeComment}
                setActiveCommentId={setActiveCommentId}
              />
            </>
          ) : (
            <div className={styles.sidebarContent}>
              {!hasNoComments &&
                sidebarTab === "active" &&
                renderActiveComments()}
              {!hasNoComments &&
                sidebarTab === "resolved" &&
                renderResolvedComments()}
            </div>
          )}
        </div>
        <iframe ref={iframeRef} src={iframeSrc} />
      </main>
    </div>
  );
}
